import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const FadeUp = ({ children, delay = 0, className = "", y = 30 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <motion.span
    className={"block overflow-hidden " + className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.35 }}
  >
    <motion.span
      className="block"
      variants={{
        hidden: { y: "112%" },
        visible: {
          y: 0,
          transition: { duration: 1.05, delay, ease: EASE },
        },
      }}
    >
      {children}
    </motion.span>
  </motion.span>
);

export const HeroLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroFade = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, className = "", light = false }) => (
  <div className={"flex items-center gap-4 " + className}>
    <span className="h-px w-10 bg-gold/60" />
    <span className="label-cap">{children}</span>
    {light && <span className="h-px w-10 bg-gold/60" />}
  </div>
);

// Linea oro che si disegna da sinistra quando entra in vista
export const DrawLine = ({ className = "", delay = 0, width = "w-16" }) => (
  <motion.div
    className={`${width} h-px origin-left bg-gold/70 ${className}`}
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true, amount: 0.6 }}
    transition={{ duration: 1.1, delay, ease: EASE }}
  />
);

// Contenitore che rivela i figli in cascata durante lo scroll
export const Stagger = ({ children, className = "", gap = 0.07, as = "div" }) => {
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: gap } } }}
    >
      {children}
    </Tag>
  );
};

export const StaggerItem = ({ children, className = "", as = "div", y = 24 }) => {
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
      }}
    >
      {children}
    </Tag>
  );
};

// Immagine con leggero parallasse durante lo scroll
export const ParallaxImg = ({ src, alt, className = "", speed = 8 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed}%`, `${speed}%`]);

  return (
    <div ref={ref} className={"relative overflow-hidden " + className}>
      <motion.div style={{ y, scale: 1.18 }} className="h-full w-full">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </motion.div>
    </div>
  );
};
